/**
 * People → Employees list.
 *
 * Modernized:
 *   - Reads from /api/employees (slim picker DTO; same endpoint that
 *     powers the EmployeePicker) with LE filter respecting the
 *     top-bar scope. "All entities" → no filter (all LEs visible).
 *   - Uses <DataTable /> primitive so HR gets global search, per-
 *     column filters, column visibility, sort, multi-sort, and
 *     CSV/Excel export for free.
 *   - Rows show <EmployeeAvatar /> + name + code + nationality +
 *     hire date + status. Click any row → master file.
 */

import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { UserPlus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { Badge } from "../components/Badge";
import { ErrorPanel, Loading } from "../components/Loading";
import { ProbationWidget } from "../components/ProbationWidget";
import { DataTable, PageHeader } from "../components/ui";
import { EmployeeAvatar } from "../components/ui/EmployeeAvatar";
import { api, ApiError } from "../lib/api";
import type { EmployeePickerOption } from "../lib/api";
import {
  ALL_ENTITIES,
  useLegalEntityGate,
  useLegalEntityScope,
} from "../lib/legalEntity";

function statusTone(s: string): "green" | "slate" | "amber" | "rose" {
  switch (s) {
    case "active":
      return "green";
    case "on_leave":
    case "suspended":
      return "amber";
    case "terminated":
      return "rose";
    default:
      return "slate";
  }
}

export function EmployeeListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { legalEntityId: le, gate } = useLegalEntityGate();
  const scope = useLegalEntityScope();
  const isAllScope = scope === ALL_ENTITIES;
  const leFilter = isAllScope ? undefined : le;

  const employeesQ = useQuery({
    queryKey: ["employees-list", leFilter ?? "_all_"],
    queryFn: () =>
      api.listEmployeesForPicker({
        legalEntityId: leFilter,
        includeTerminated: true,
        limit: 2000,
      }),
    enabled: !!le,
  });

  const allRows = employeesQ.data ?? [];
  // Status filter — opt-in pill that surfaces every distinct
  // employment_status (active / terminated / on_leave / suspended).
  // Used by UI_EMP_005 to assert HR can pivot to ex-employees.
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const rows = useMemo(
    () =>
      statusFilter === "all"
        ? allRows
        : allRows.filter((e) => e.employment_status === statusFilter),
    [allRows, statusFilter],
  );
  const activeCount = allRows.filter(
    (e) => e.employment_status !== "terminated",
  ).length;
  const totalCount = allRows.length;

  const columns = useMemo<ColumnDef<EmployeePickerOption, unknown>[]>(
    () => [
      {
        id: "photo",
        header: "",
        cell: ({ row }) => (
          <EmployeeAvatar
            employeeId={row.original.id}
            photoUrl={row.original.photo_url}
            firstName={row.original.first_name_en}
            lastName={row.original.last_name_en}
            size={32}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        size: 48,
      },
      // The 6 data columns below use locale-aware headers via t().
      // useMemo re-evaluates when t() identity changes (i18n locale
      // switch), so headers refresh on language toggle.
      {
        accessorKey: "employee_code",
        header: t("employee.col_code"),
        cell: ({ row }) => (
          <span className="font-mono text-xs text-text-secondary">
            {row.original.employee_code}
          </span>
        ),
        meta: { filterType: "text" },
      },
      {
        id: "name_en",
        header: t("employee.col_name_en"),
        accessorFn: (e) => `${e.first_name_en} ${e.last_name_en}`,
        meta: { filterType: "text" },
      },
      {
        id: "name_ar",
        header: t("employee.col_name_ar"),
        accessorFn: (e) => `${e.first_name_ar} ${e.last_name_ar}`,
        cell: ({ row }) => (
          <span dir="rtl">
            {row.original.first_name_ar} {row.original.last_name_ar}
          </span>
        ),
        meta: { filterType: "text" },
      },
      {
        accessorKey: "nationality",
        header: t("employee.col_nationality"),
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.nationality}
            {row.original.is_saudi_national ? (
              <Badge tone="green">{t("employee.badge_sa")}</Badge>
            ) : null}
          </span>
        ),
        meta: { filterType: "select" },
      },
      {
        accessorKey: "hire_date",
        header: t("employee.col_hire_date"),
        cell: ({ getValue }) => (
          <span className="tabular-nums text-sm">
            {String(getValue() ?? "—")}
          </span>
        ),
      },
      {
        accessorKey: "employment_status",
        header: t("employee.col_status"),
        cell: ({ getValue }) => (
          <Badge tone={statusTone(String(getValue()))}>
            {String(getValue())}
          </Badge>
        ),
        meta: { filterType: "select" },
      },
    ],
    [t],
  );

  if (gate) return gate;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("employee.list_header")}
        subtitle={
          isAllScope
            ? t("employee.subtitle_all_scope")
            : t("employee.subtitle_scoped")
        }
        breadcrumbs={[
          { label: t("employee.breadcrumb_people"), href: "/people" },
          { label: t("employee.breadcrumb_employees") },
        ]}
        actions={
          <Link
            to="/people/employees/new"
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-blue px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            <UserPlus className="h-4 w-4" /> {t("employee.cta_hire")}
          </Link>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:flex sm:items-center sm:gap-6">
        <div className="rounded-lg border border-border-subtle bg-surface-default px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-text-tertiary">
            {t("employee.count_active")}
          </p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-text-primary">
            {employeesQ.isLoading ? "—" : activeCount}
          </p>
        </div>
        <div className="rounded-lg border border-border-subtle bg-surface-default px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-text-tertiary">
            {t("employee.count_total")}
          </p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-text-tertiary">
            {employeesQ.isLoading ? "—" : totalCount}
          </p>
        </div>
      </div>

      {/* Status filter row. Triggered + cleared via stable
       * data-testid hooks so UI_EMP_005 can pivot to terminated. */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-xs font-medium uppercase tracking-widest text-text-tertiary">
          {t("employee.filter_status_label", "Status")}
        </span>
        <div className="relative">
          <button
            type="button"
            data-testid="employees-filter-status"
            onClick={() => setStatusFilterOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={statusFilterOpen}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border-subtle bg-surface-default px-3 py-1.5 text-sm hover:bg-surface-sunken"
          >
            {statusFilter === "all"
              ? t("employee.filter_status_all", "All statuses")
              : t(`employee.filter_status_${statusFilter}`, statusFilter)}
          </button>
          {statusFilterOpen && (
            <ul
              role="listbox"
              className="absolute left-0 top-full z-10 mt-1 w-44 rounded-lg border border-border-subtle bg-surface-default shadow-lg"
            >
              {[
                { v: "all", labelKey: "employee.filter_status_all", fallback: "All statuses" },
                { v: "active", labelKey: "employee.filter_status_active", fallback: "Active" },
                { v: "terminated", labelKey: "employee.filter_status_terminated", fallback: "Terminated" },
                { v: "on_leave", labelKey: "employee.filter_status_on_leave", fallback: "On leave" },
                { v: "suspended", labelKey: "employee.filter_status_suspended", fallback: "Suspended" },
              ].map((opt) => (
                <li
                  key={opt.v}
                  role="option"
                  aria-selected={statusFilter === opt.v}
                  tabIndex={0}
                  onClick={() => {
                    setStatusFilter(opt.v);
                    setStatusFilterOpen(false);
                  }}
                  className="cursor-pointer px-3 py-1.5 text-sm hover:bg-surface-sunken aria-selected:bg-brand-blue/10 aria-selected:text-brand-blue"
                >
                  {t(opt.labelKey, opt.fallback)}
                </li>
              ))}
            </ul>
          )}
        </div>
        {statusFilter !== "all" && (
          <>
            <span
              data-testid="employees-filter-status-active"
              className="inline-flex items-center gap-1 rounded-full bg-brand-blue/10 px-2 py-0.5 text-xs font-medium text-brand-blue"
            >
              {t(`employee.filter_status_${statusFilter}`, statusFilter)}
            </span>
            <button
              type="button"
              data-testid="employees-filter-clear"
              onClick={() => setStatusFilter("all")}
              className="inline-flex items-center gap-1 rounded border border-border-subtle px-2 py-0.5 text-xs hover:bg-surface-sunken"
            >
              <X className="h-3 w-3" /> {t("employee.filter_clear", "Clear")}
            </button>
          </>
        )}
      </div>

      <ProbationWidget />

      {employeesQ.isError && (
        <ErrorPanel
          message={(employeesQ.error as ApiError).message}
        />
      )}
      {employeesQ.isLoading ? (
        <Loading />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          exportFilename="employees"
          onRowClick={(r) => navigate(`/people/employees/${r.id}`)}
        />
      )}
    </div>
  );
}
